/**
 * Event Profile API — backend calls for the V2 event profile page.
 *
 * READ (apiFetch):
 *   getevent                   (main event data)
 *   geteventnotes              (event notes list)
 *   geteventlogs               (lifecycle logs — Cloud only)
 *   getreasonedits             (reason edit history)
 *   core_preform_event_notes   (preformatted note options)
 *   core_reason_codes          (reason code dropdown)
 *
 * WRITE (apiSave):
 *   core_event_notes PUT       (add note)
 *   core_event_notes POST      (edit note)
 *   core_event_notes DELETE    (delete note)
 *
 * WRITE (apiTransaction):
 *   event_reason_edit          (change reason code)
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';
import { markEventsChanged } from '../../../../services/reportCache.js';

// ---------------------------------------------------------------------------
// READ — Event data
// ---------------------------------------------------------------------------

/** Fetch main event record. */
export function fetchEvent(eventId, campusId) {
    return apiFetch({
        procedure: 'getevent',
        reqType: 'stored',
        payload: { event_id: eventId, campus_id: campusId },
        readOnly: true,
    });
}

/** Fetch event notes. */
export function fetchEventNotes(eventId, campusId) {
    return apiFetch({
        procedure: 'geteventnotes',
        reqType: 'stored',
        payload: { event_id: eventId, campus_id: campusId },
    });
}

/** Fetch preformatted note templates. */
export function fetchPreformNotes(campusId) {
    return apiFetch({
        table: 'core_preform_event_notes',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
        readOnly: true,
    });
}

/** Fetch event grandstream call logs (Grandstream only). */
export function fetchEventCallLogs(eventId, campusId) {
    return apiFetch({
        procedure: 'geteventcalllogs',
        reqType: 'stored',
        payload: { event_id: eventId, campus_id: campusId },
        readOnly: true,
    });
}

/** Fetch event lifecycle logs (Cloud only). */
export function fetchEventLogs(eventId) {
    return apiFetch({
        procedure: 'geteventlogs',
        reqType: 'stored',
        payload: { event_id: eventId },
        readOnly: true,
    });
}

/** Fetch available reason codes for the campus. */
export function fetchReasonCodes(campusId) {
    return apiFetch({
        table: 'core_reason_codes',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
        readOnly: true,
    });
}

/** Fetch reason edit history for an event. */
export function fetchReasonEdits(eventId, campusId) {
    return apiFetch({
        procedure: 'getreasonedits',
        reqType: 'stored',
        payload: { event_id: eventId, campus_id: campusId },
    });
}

// ---------------------------------------------------------------------------
// WRITE — Notes
// ---------------------------------------------------------------------------

/** Add a new note to an event. */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section C): Admin+; notes must carry the request's campus. */
export function addEventNote(eventId, campusId, note, userId, clientId) {
    /* Tell any cached report that contains these events that it is stale, so
       coming back to it shows this edit rather than the rows from before it.
       Marked on attempt, not on success: a false-positive stale costs
       one refresh of identical data, a missed one shows the user a
       report that contradicts the edit they just made.
       See docs/REPORT_STATE_RESTORE_PLAN.md O1. */
    markEventsChanged(eventId);
    return apiTransaction({
        transType: 'event_note_add',
        payload: [{ event_id: eventId, campus_id: campusId, note, modified_by: userId }],
        campusId,
        clientId,
        userId,
    });
}

/** Edit an existing note. */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section C): Admin+; notes must carry the request's campus. Only the note text is editable. */
export function editEventNote(noteId, eventId, campusId, note, userId, clientId) {
    return apiTransaction({
        transType: 'event_note_edit',
        payload: [{ note_id: noteId, event_id: eventId, campus_id: campusId, note, modified_by: userId }],
        campusId,
        clientId,
        userId,
    });
}

/** Delete a note. */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section C): Admin+; notes must carry the request's campus. */
export function deleteEventNote(noteId, userId, campusId) {
    return apiTransaction({
        transType: 'event_note_delete',
        payload: [{ note_id: noteId, modified_by: userId }],
        campusId,
        userId,
    });
}

// ---------------------------------------------------------------------------
// WRITE — Reason
// ---------------------------------------------------------------------------

/** Edit event reason code (transaction). */
export function editEventReason(eventId, newReasonId, oldReasonId, campusId, userId, clientId) {
    /* Tell any cached report that contains these events that it is stale, so
       coming back to it shows this edit rather than the rows from before it.
       Marked on attempt, not on success: a false-positive stale costs
       one refresh of identical data, a missed one shows the user a
       report that contradicts the edit they just made.
       See docs/REPORT_STATE_RESTORE_PLAN.md O1. */
    markEventsChanged(eventId);
    return apiTransaction({
        transType: 'event_reason_edit',
        payload: [{ event_id: eventId, reason_id: newReasonId }],
        oldPayload: [{ event_id: eventId, reason_id: oldReasonId }],
        campusId,
        userId,
        clientId,
    });
}
